/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 *
 * This Material DayJs date adapter is based of @angular/material-moment-adapter to work with DayJs.
 * We've had to create out own adapter as angular components does not supply an adapter for DayJs and the
 * one we previously used @tabuckner/material-dayjs-adapter is no longer maintained and is in a broken state.
 *
 * While most of the functionality is working due to some core differences in how moment and DayJS
 * certain features do not work, one being this adapter only supports en and en-nz locales,
 * also setting the locale will set the global scope for DayJs
 *
 * The module should be checked and updated if necesary anytime Angular, Angular material or DayJs is updated.
 */
import dayjs, { ConfigType, Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';

import { environment } from '@environments/environment';

import { Injectable, InjectionToken, inject } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';

export interface DayJsDateAdapterOptions {
  /**
   * When enabled, the dates have to match the format exactly.
   * See https://day.js.org/docs/en/parse/string-format.
   */
  strict?: boolean;

  /**
   * Turns the use of utc dates on or off.
   * Changing this will change how Angular Material components like DatePicker output dates.
   * {@default false}
   */
  useUtc?: boolean;
}

/** InjectionToken for Dayjs date adapter to configure options. */
export const MAT_DAYJS_DATE_ADAPTER_OPTIONS = new InjectionToken<DayJsDateAdapterOptions>(
  'MAT_DAYJS_DATE_ADAPTER_OPTIONS',
  {
    providedIn: 'root',
    factory: MAT_DAYJS_DATE_ADAPTER_OPTIONS_FACTORY,
  },
);

/** @docs-private */
export function MAT_DAYJS_DATE_ADAPTER_OPTIONS_FACTORY(): DayJsDateAdapterOptions {
  return {
    useUtc: false,
  };
}

/** Creates an array and fills it with values. */
function range<T>(length: number, valueFunction: (index: number) => T): T[] {
  const valuesArray = Array(length);
  for (let i = 0; i < length; i++) {
    valuesArray[i] = valueFunction(i);
  }
  return valuesArray;
}

/** Adapts Dayjs Dates for use with Angular Material. */
@Injectable()
export class DayjsDateAdapter extends DateAdapter<Dayjs> {
  private _options = inject<DayJsDateAdapterOptions>(MAT_DAYJS_DATE_ADAPTER_OPTIONS, { optional: true });

  private _localeData!: {
    firstDayOfWeek: number;
    longMonths: string[];
    shortMonths: string[];
    dates: string[];
    longDaysOfWeek: string[];
    shortDaysOfWeek: string[];
    narrowDaysOfWeek: string[];
  };

  constructor() {
    const dateLocale = inject(MAT_DATE_LOCALE, { optional: true });

    super();
    this._initializeParser((dateLocale as string) || dayjs.locale());
  }

  override setLocale(locale: string) {
    super.setLocale(locale);
    dayjs.locale(locale);

    const dayJsLocaleData = dayjs.localeData();
    this._localeData = {
      firstDayOfWeek: dayJsLocaleData.firstDayOfWeek(),
      longMonths: dayJsLocaleData.months(),
      shortMonths: dayJsLocaleData.monthsShort(),
      dates: range(31, i => this.createDate(2017, 0, i + 1).format('D')),
      longDaysOfWeek: dayJsLocaleData.weekdays(),
      shortDaysOfWeek: dayJsLocaleData.weekdaysShort(),
      narrowDaysOfWeek: dayJsLocaleData.weekdaysMin(),
    };
  }

  getYear(date: Dayjs): number {
    return this._createDayJs(date).year();
  }

  getMonth(date: Dayjs): number {
    return this._createDayJs(date).month();
  }

  getDate(date: Dayjs): number {
    return this._createDayJs(date).date();
  }

  getDayOfWeek(date: Dayjs): number {
    return this._createDayJs(date).day();
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    // DayJs doesn't support narrow month names, so we just use short if narrow is requested.
    return style == 'long' ? this._localeData.longMonths : this._localeData.shortMonths;
  }

  getDateNames(): string[] {
    return this._localeData.dates;
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    if (style == 'long') {
      return this._localeData.longDaysOfWeek;
    }
    if (style == 'short') {
      return this._localeData.shortDaysOfWeek;
    }
    return this._localeData.narrowDaysOfWeek;
  }

  getYearName(date: Dayjs): string {
    return this._createDayJs(date).format('YYYY');
  }

  getFirstDayOfWeek(): number {
    return this._localeData.firstDayOfWeek;
  }

  getNumDaysInMonth(date: Dayjs): number {
    return this._createDayJs(date).daysInMonth();
  }

  clone(date: Dayjs): Dayjs {
    return date.clone();
  }

  createDate(year: number, month: number, date: number): Dayjs {
    // DayJs will create an invalid date if any of the components are out of bounds, but we
    // explicitly check each case so we can throw more descriptive errors.
    if (environment.production !== true) {
      if (month < 0 || month > 11) {
        throw Error(`Invalid month index "${month}". Month index has to be between 0 and 11.`);
      }

      if (date < 1) {
        throw Error(`Invalid date "${date}". Date has to be greater than 0.`);
      }

      if (date < 1) {
        throw Error(`Invalid date "${date}". Date has to be greater than 0.`);
      }
    }

    const result = this._createDayJs().set('year', year).set('month', month).set('date', date);

    // If the result isn't valid, the date must have been out of bounds for this month.
    if (!result.isValid() && environment.production !== true) {
      throw Error(`Invalid date "${date}" for month with index "${month}".`);
    }

    return result;
  }

  today(): Dayjs {
    return this._createDayJs();
  }

  parse(value: any, parseFormat: string): Dayjs | null {
    if (value && typeof value == 'string') {
      return this._createDayJs(value, parseFormat, this.locale);
    }
    return value ? this._createDayJs(value) : null;
  }

  format(date: Dayjs, displayFormat: string): string {
    date = this._createDayJs(date);
    if (!this.isValid(date) && environment.production !== true) {
      throw Error('DayJsDateAdapter: Cannot format invalid date.');
    }
    return date.format(displayFormat);
  }

  addCalendarYears(date: Dayjs, years: number): Dayjs {
    return this._createDayJs(date).add(years, 'year');
  }

  addCalendarMonths(date: Dayjs, months: number): Dayjs {
    return this._createDayJs(date).add(months, 'month');
  }

  addCalendarDays(date: Dayjs, days: number): Dayjs {
    return this._createDayJs(date).add(days, 'day');
  }

  toIso8601(date: Dayjs): string {
    return this._createDayJs(date).toISOString();
  }

  /**
   * Returns the given value if given a valid DayJs or null. Deserializes valid ISO 8601 strings
   * (https://www.ietf.org/rfc/rfc3339.txt) and valid Date objects into valid DayJs and empty
   * string into null. Returns an invalid date for all other values.
   */

  override deserialize(value: any): Dayjs | null {
    let date;
    if (value instanceof Date) {
      date = this._createDayJs(value);
    } else if (this.isDateInstance(value)) {
      // Note: assumes that cloning also sets the correct locale.
      return this._createDayJs(value);
    }
    if (typeof value === 'string') {
      if (!value) {
        return null;
      }
      date = this._createDayJs(value).format();
    }
    if (date && this.isValid(this._createDayJs(date))) {
      return this._createDayJs(date);
    }
    return super.deserialize(value);
  }

  isDateInstance(obj: any): boolean {
    return dayjs.isDayjs(obj);
  }

  isValid(date: Dayjs): boolean {
    return this._createDayJs(date).isValid();
  }

  invalid(): Dayjs {
    return this._createDayJs(null);
  }

  /** Creates a DayJs instance while respecting the current UTC settings. */
  private _createDayJs(date?: ConfigType, format?: string, locale?: string): Dayjs {
    const { strict, useUtc }: DayJsDateAdapterOptions = this._options || {};

    return useUtc ? dayjs.utc(date, format, strict) : dayjs(date, format, locale, strict);
  }

  private _initializeParser(dateLocale: string) {
    const { useUtc }: DayJsDateAdapterOptions = this._options || {};
    if (useUtc) {
      dayjs.extend(utc);
    }

    dayjs.extend(LocalizedFormat);
    dayjs.extend(customParseFormat);
    dayjs.extend(localeData);

    this.setLocale(dateLocale);
  }
}
